import {
  Alert,
  Badge,
  Button,
  Container,
  Grid,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Stepper,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notify } from '@/lib/notify';
import { IconAlertCircle, IconArrowLeft, IconChevronDown, IconEdit, IconFileText } from '@tabler/icons-react';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile, useQuestionnaireForm } from '@medplum/react-hooks';
import { getPaymentTypeCode, type PaymentTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { getPatientHomeState } from '@unify/fhir-model/resources/patient';
import {
  parseTreatmentPlanDraft,
  setTreatmentPlanGenerationProvenance,
  syncTreatmentPlanExpirationExtension,
  type TreatmentPlanGenerationProvenance,
} from '@unify/fhir-model/resources/treatment-plan';
import { useGeminiAiOptOut } from '../../hooks/useGeminiAiOptOut';
import { practitionerPreferredReference } from '../../lib/loadPractitionerPreferredLabels';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RecordPageHeader } from '../../components/RecordPageHeader';
import { SignatureCaptureModal } from '../../components/SignatureCapture';
import { useClients } from '../../contexts/ClientsContext';
import { useEpisodeScopedClientChartBundle } from '../../hooks/useEpisodeScopedClientChartBundle';
import { useClientActiveTreatmentPlanDiagnoses } from '../../hooks/useClientActiveTreatmentPlanDiagnoses';
import { getPatientAccountRefs } from '../../lib/clientFacingTasks';
import {
  CHART_DOCUMENT_TASK_IDENTIFIER_SYSTEMS,
  chartDocumentNeedsNewQrToResend,
  resentChartDocumentCopy,
  supersedeResentChartDocument,
  waitForTaskForQr,
} from '../../lib/clientAssignmentTasks';
import { ActiveTreatmentPlanDiagnosesBanner } from './components/ActiveTreatmentPlanDiagnosesBanner';
import { readQuestionnaireForResponseRef } from '../../lib/chart/chartQuestionnaireRef';
import {
  applyChartDocumentEpisode,
  applyChartDocumentSignSettings,
  applyChartDocumentTemplateCategoryTag,
  applyProviderSignature,
  buildInitialChartDocumentResponse,
  CHART_DOCUMENT_STATUS_LABELS,
  clearChartDocumentClientSignature,
  clearChartDocumentProviderSignatures,
  computeProviderLockedLinkIds,
  getChartDocumentClientSignature,
  getChartDocumentEpisodeId,
  getChartDocumentStatus,
  getProviderLockedLinkIds,
  isChartDocumentClientSignatureRequired,
  isHistoricalMigrationQuestionnaireResponse,
  preserveChartDocumentBasedOn,
  setChartDocumentStatus,
  setProviderLockedLinkIds,
  type ChartDocumentStatus,
} from '../../lib/chart/chartDocumentWorkflow';
import { SignatureDisplayBox } from '../../components/SignatureDisplayBox';
import {
  buildNestedQuestionnaireResponseItemsForSave,
  shapeNestedResponseItemsForQuestionnaire,
} from '../appointments/utils/questionnaireResponseShape';
import type { SignatureInfo } from '../appointments/fhirNoteMapping';
import {
  getSignatureFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
} from '../appointments/fhirNoteMapping';
import { isTreatmentPlanCosignTemplate, usesSpecializedTreatmentPlanForm } from '../../lib/chart/chartDocumentTemplates';
import {
  canAuthorClinicalDocumentationForEpisode,
  chartScopeFromRoleAndPractitioner,
  clinicalDocumentationAuthorRole,
  resolveChartDocumentEpisodeId,
} from '../../lib/rbac/eocChartScope';
import { usePermissions } from '../../contexts/PermissionContext';
import { useCurrentUserPractitioner } from '../../hooks/useCurrentUserPractitioner';
import { useSupervisionScope } from '../supervision/hooks/useSupervisionScope';
import { syncTreatmentPlanCarePlan } from '../../lib/chart/syncTreatmentPlanCarePlan';
import { hydrateTreatmentPlanQrFromCarePlan } from '../../lib/chart/hydrateTreatmentPlanQrFromCarePlan';
import {
  isCoordinationOfCareRoiTemplate,
  mergeCoordinationRoiPrefillIntoResponse,
} from '../../lib/chart/coordinationOfCareRoi';
import {
  chartDocumentSaveActionRequiresFieldValidation,
  validateChartDocumentBeforeSign,
} from '../../lib/chart/chartDocumentFormValidation';
import {
  type ChartDocumentSaveAction,
  chartDocumentSaveSuccessMessage,
  filterChartDocumentSaveActions,
  saveActionNeedsSignature,
  saveActionSendsToClient,
  SEND_FOR_CLIENT_SIGNATURE_ACTION,
  sendToClientConfirmCopy,
  signSettingsForSaveAction,
} from '../../lib/chart/chartDocumentSaveActions';
import {
  validateRequiredTemplateFieldsInScope,
} from '../../lib/questionnaireResponseFillValidation';
import { scrollToAndFlashQuestionnaireItem } from '../appointments/utils/scrollToQuestionnaireItem';
import { resentChartDocumentHref } from './utils/chartDocumentResend';
import { isClientShareRestrictedTemplate } from '../../lib/clientShareRestrictedTemplates';
import { useOptionalAiAssistant } from '../../features/ai-assistant';
import { TreatmentPlanDocumentFormSection } from './components/TreatmentPlanDocumentFormSection';
import { AiTreatmentPlanGeneratorPanel } from './components/AiTreatmentPlanGeneratorPanel';
import { TreatmentPlanQualityPanel } from './components/TreatmentPlanQualityPanel';
import { toEpisodeSelectOptions } from './data/episodeSelectOptions';
import type { TreatmentPlanRevisionSuggestion } from '../../lib/treatmentPlanAiRevision';
import type { TreatmentPlanGeneratorSuccessResult } from '../../lib/aiTreatmentPlanGeneratorService';
import {
  diagnosesFromTreatmentPlanDraftCodes,
  symptomsFromTreatmentPlanUiTexts,
  type TreatmentPlanOperation,
} from '../../lib/treatment-plan/evidenceGroundedSymptoms';
import { useProviderNoteAutosave } from '../appointments/hooks/useProviderNoteAutosave';
import { NoteAutosaveStatus } from '@/components/NoteAutosaveStatus';
import {
  buildTreatmentPlanAutosaveSnapshot,
  treatmentPlanHasProviderContent,
} from '../../lib/treatmentPlanAutosave';
import {
  PreviewItemArray,
} from '../settings/template-library/components/TemplatePreviewDrawer';
import { DocumentFormatFooter } from '../settings/template-library/components/DocumentFormatBranding';
import { useDocumentFormatPresentation } from '../settings/template-library/hooks';
import {
  resolveQuestionnaireVariables,
  useVariableContext,
  type VariableContext,
} from '../../lib/template-variables';
import { getUserFacingErrorMessage, UserFacingError } from '@/lib/userFacingError';

function statusBadgeColor(status: ChartDocumentStatus): string {
  switch (status) {
    case 'completed':
      return 'green';
    case 'pending-client':
      return 'orange';
    case 'cancelled':
      return 'gray';
    case 'error':
      return 'red';
    default:
      return 'yellow';
  }
}

interface ChartDocumentFormSectionProps {
  questionnaire: Questionnaire;
  formSeed: QuestionnaireResponse | null;
  readOnlyLinkIds: Set<string>;
  isEditable: boolean;
  varCtx: VariableContext;
  patientId: string;
  /** When true, paginated Next does not enforce required fields (client completes after send). */
  deferRequiredFieldValidation: boolean;
  onLiveResponseChange: (response: QuestionnaireResponse | undefined) => void;
  /** Fired on every actual form mutation (not renders) — drives autosave scheduling. */
  onFormChange?: () => void;
}

function ChartDocumentFormSection({
  questionnaire,
  formSeed,
  readOnlyLinkIds,
  isEditable,
  varCtx,
  patientId,
  deferRequiredFieldValidation,
  onLiveResponseChange,
  onFormChange,
}: ChartDocumentFormSectionProps): JSX.Element {
  const formDefaultValue = useMemo(() => {
    if (!formSeed) return undefined;
    return {
      ...formSeed,
      item: shapeNestedResponseItemsForQuestionnaire(formSeed.item, questionnaire) ?? formSeed.item,
    };
  }, [formSeed, questionnaire]);

  const formState = useQuestionnaireForm({
    questionnaire,
    defaultValue: formDefaultValue,
    disablePagination: false,
    onChange: onFormChange,
  });

  useEffect(() => {
    if ('loading' in formState && formState.loading) {
      onLiveResponseChange(formSeed ?? undefined);
      return;
    }
    if ('questionnaireResponse' in formState) {
      onLiveResponseChange(formState.questionnaireResponse);
    }
  }, [formSeed, formState, onLiveResponseChange]);

  if ('loading' in formState && formState.loading) {
    return <Text size="sm" c="dimmed">Loading form…</Text>;
  }

  if (formState.items.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        This template has no fields to complete.
      </Text>
    );
  }

  if (formState.pagination) {
    const { pages, activePage, onNextPage, onPrevPage } = formState;
    const showBack = activePage > 0;
    const showNext = activePage < pages.length - 1;

    return (
      <form onSubmit={(e) => e.preventDefault()}>
        <Stack gap="md">
          <Stepper active={activePage} allowNextStepsSelect={false} size="sm">
            {pages.map((page, pageIndex) => (
              <Stepper.Step key={page.linkId} label={page.title}>
                {pageIndex === activePage && (
                  <PreviewItemArray
                    formState={formState}
                    context={[]}
                    items={formState.items}
                    responseItems={formState.responseItems}
                    questionnaireItems={questionnaire.item}
                    variableContext={varCtx}
                    readOnlyLinkIds={readOnlyLinkIds}
                    readOnly={!isEditable}
                    patientId={patientId}
                  />
                )}
              </Stepper.Step>
            ))}
          </Stepper>
          <Group justify="center" gap="sm" mt="md">
            {showBack && (
              <Button variant="default" size="sm" onClick={onPrevPage}>
                Back
              </Button>
            )}
            {showNext && (
              <Button
                size="sm"
                onClick={(e) => {
                  const form = e.currentTarget.closest('form') as HTMLFormElement;
                  if (!form.reportValidity()) return;
                  if (!deferRequiredFieldValidation) {
                    const pageValidation = validateRequiredTemplateFieldsInScope(
                      questionnaire,
                      formState.questionnaireResponse,
                      formState.items,
                    );
                    if (!pageValidation.success) {
                      scrollToAndFlashQuestionnaireItem(pageValidation.missingItemPath);
                      notify({
                        type: 'warning',
                        title: 'Required fields missing',
                        // eslint-disable-next-line no-restricted-syntax -- authored validation text from our own code, not a caught error
                        message: pageValidation.message,
                      });
                      return;
                    }
                  }
                  onNextPage();
                }}
              >
                Next
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    );
  }

  return (
    <PreviewItemArray
      formState={formState}
      context={[]}
      items={formState.items}
      responseItems={formState.responseItems}
      questionnaireItems={questionnaire.item}
      variableContext={varCtx}
      readOnlyLinkIds={readOnlyLinkIds}
      readOnly={!isEditable}
      patientId={patientId}
    />
  );
}

export function ChartDocumentPage(): JSX.Element {
  const { id: clientId, responseId } = useParams<{ id: string; responseId?: string }>();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const initialEpisodeId = searchParams.get('episodeId') ?? undefined;
  const carePlanVersionId = searchParams.get('carePlanVersionId') ?? undefined;
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { getClientById } = useClients();

  const geminiAiDisabled = useGeminiAiOptOut(clientId, profile?.id);
  const aiAssistant = useOptionalAiAssistant();
  const { bundle: chartBundle, episodeInteraction } = useEpisodeScopedClientChartBundle(clientId);
  const { role, availableRoles } = usePermissions();
  const { practitioner: viewerPractitioner } = useCurrentUserPractitioner();
  const { superviseePractitionerIds } = useSupervisionScope();

  const client = clientId ? getClientById(clientId) : undefined;
  const chartHref = clientId ? `/dashboard/clients/${clientId}?tab=chart` : '/dashboard/caseload';

  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [savedResponse, setSavedResponse] = useState<QuestionnaireResponse | null>(null);
  const [clientJurisdictionState, setClientJurisdictionState] = useState<string>();
  /** Render-assigned latest saved response — async save paths must read this, not a stale closure. */
  const savedResponseRef = useRef<QuestionnaireResponse | null>(null);
  // Never downgrade a persisted document back to null. `persistResponse` sets this ref
  // imperatively and then calls setSavedResponse; any render landing before that state
  // commits would otherwise clear the id, and the next save would take the create branch
  // again — which is how a single draft turned into thousands of duplicate resources.
  if (savedResponse || !savedResponseRef.current) {
    savedResponseRef.current = savedResponse;
  }
  /**
   * Identity of the mounted form session. Stays fixed when autosave creates the
   * document and the URL switches from /new to /:responseId, so the form does
   * not remount (which would drop focus and any keystrokes typed mid-save).
   */
  const formSessionKeyRef = useRef<string>('new');
  /**
   * Stable AI-assistant editor identity for this ChartDocumentPage mount.
   * Survives /new → persisted QuestionnaireResponse id so Quick/Guided sessions
   * are not reset by autosave URL pinning. Distinct from documentKey.
   */
  const treatmentPlanEditorSessionKeyRef = useRef<string>(
    responseId ? `qr:${responseId}` : `new:${crypto.randomUUID()}`,
  );
  const [liveResponse, setLiveResponse] = useState<QuestionnaireResponse | undefined>(undefined);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | undefined>(initialEpisodeId);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    void medplum.readResource('Patient', clientId).then((patient) => {
      if (!cancelled) setClientJurisdictionState(getPatientHomeState(patient));
    }).catch(() => {
      if (!cancelled) setClientJurisdictionState(undefined);
    });
    return () => { cancelled = true; };
  }, [clientId, medplum]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signOpened, { open: openSign, close: closeSign }] = useDisclosure(false);
  const [sendConfirmOpened, { open: openSendConfirm, close: closeSendConfirm }] = useDisclosure(false);
  const [pendingAction, setPendingAction] = useState<ChartDocumentSaveAction | null>(null);
  const [pendingSendAction, setPendingSendAction] = useState<ChartDocumentSaveAction | null>(null);
  const [localEditMode, setLocalEditMode] = useState(() => searchParams.get('edit') === 'true');
  /** True once autosave created the draft for a doc opened via /new — keeps "new doc" UI (guided planner) visible. */
  const [autosaveCreatedNew, setAutosaveCreatedNew] = useState(false);
  const [pendingAiSuggestion, setPendingAiSuggestion] = useState<TreatmentPlanRevisionSuggestion | null>(null);
  const [guidedGeneratedApplied, setGuidedGeneratedApplied] = useState(false);
  const liveResponseRef = useRef<QuestionnaireResponse | undefined>(undefined);
  liveResponseRef.current = liveResponse;
  /** Set when the guided AI planner populated this draft; persisted as a provenance extension on save. */
  const [aiGenerationProvenance, setAiGenerationProvenance] = useState<TreatmentPlanGenerationProvenance | null>(null);
  const [episodePaymentType, setEpisodePaymentType] = useState<PaymentTypeCode | undefined>();
  const docFormat = useDocumentFormatPresentation();
  const { context: varCtx } = useVariableContext();

  const providerEpisodes = useMemo(() => {
    const all = chartBundle?.episodes ?? [];
    if (!episodeInteraction) return all;
    return all.filter((episode) => episodeInteraction.authorizedEpisodeIds.has(episode.id));
  }, [chartBundle?.episodes, episodeInteraction]);

  const episodeSelectData = useMemo(() => toEpisodeSelectOptions(providerEpisodes), [providerEpisodes]);

  const isNew = !responseId;
  const isTreatmentPlanTemplate = questionnaire ? isTreatmentPlanCosignTemplate(questionnaire) : false;
  const treatmentPlanAuthorRole = clinicalDocumentationAuthorRole(availableRoles, role);
  const selectedProviderEpisode = providerEpisodes.find(
    (episode) => episode.id === selectedEpisodeId,
  );
  // Practice owner, practice admin, and Unify Admin roles read treatment plans across the practice but never author them, and a
  // clinical role only authors on episodes it is a member of. Guards direct links to this page.
  const mayAuthorTreatmentPlan = useMemo(
    () => Boolean(
      selectedProviderEpisode &&
      canAuthorClinicalDocumentationForEpisode(
        selectedProviderEpisode.careManagerPractitionerId,
        chartScopeFromRoleAndPractitioner(
          treatmentPlanAuthorRole,
          viewerPractitioner?.id,
          treatmentPlanAuthorRole === 'supervisor' ? superviseePractitionerIds : undefined,
        ),
      ),
    ),
    [
      selectedProviderEpisode,
      superviseePractitionerIds,
      treatmentPlanAuthorRole,
      viewerPractitioner?.id,
    ],
  );
  const isCoordinationRoiTemplate = questionnaire ? isCoordinationOfCareRoiTemplate(questionnaire) : false;
  const usesSpecializedTreatmentPlanUi = questionnaire ? usesSpecializedTreatmentPlanForm(questionnaire) : false;
  const { diagnoses: activeTreatmentPlanDiagnoses, loading: activeDiagnosesLoading } =
    useClientActiveTreatmentPlanDiagnoses(
      isNew && usesSpecializedTreatmentPlanUi ? clientId : undefined,
    );
  const availableSaveActions = useMemo(
    () => filterChartDocumentSaveActions(questionnaire ?? undefined),
    [questionnaire],
  );
  const deferRequiredFieldValidation = useMemo(
    () => availableSaveActions.some((entry) => saveActionSendsToClient(entry.action)),
    [availableSaveActions],
  );
  const documentStatus = savedResponse ? getChartDocumentStatus(savedResponse) : 'draft';
  const isHistoricalImport = Boolean(
    savedResponse && isHistoricalMigrationQuestionnaireResponse(savedResponse),
  );
  // Historical SP imports are immutable — never enter Edit / sign flows in Unify.
  // Opening a prior CarePlan version is also read-only.
  const isEditable =
    !isHistoricalImport
    && !carePlanVersionId
    && (!isTreatmentPlanTemplate || mayAuthorTreatmentPlan)
    && (documentStatus === 'draft' || (localEditMode && isTreatmentPlanTemplate));
  const blockedTreatmentPlanAuthoring = isNew && isTreatmentPlanTemplate && !mayAuthorTreatmentPlan;
  const canStartEditingTreatmentPlan =
    !isHistoricalImport
    && !carePlanVersionId
    && isTreatmentPlanTemplate
    && mayAuthorTreatmentPlan
    && savedResponse
    && !isEditable
    && documentStatus !== 'cancelled'
    && documentStatus !== 'error';

  const readOnlyLinkIds = useMemo(() => {
    if (!savedResponse || isEditable) return new Set<string>();
    return getProviderLockedLinkIds(savedResponse);
  }, [savedResponse, isEditable]);

  useEffect(() => {
    setLocalEditMode(searchParams.get('edit') === 'true');
  }, [responseId, searchParams]);

  useEffect(() => {
    const savedEpisodeId = savedResponse ? getChartDocumentEpisodeId(savedResponse) : undefined;
    const resolvedEpisodeId = resolveChartDocumentEpisodeId(
      savedEpisodeId,
      initialEpisodeId,
      selectedEpisodeId,
      new Set(providerEpisodes.map((episode) => episode.id)),
      providerEpisodes[0]?.id,
    );
    if (resolvedEpisodeId) {
      setSelectedEpisodeId(resolvedEpisodeId);
    }
  }, [initialEpisodeId, providerEpisodes, savedResponse, selectedEpisodeId]);

  useEffect(() => {
    if (!selectedEpisodeId) {
      setEpisodePaymentType(undefined);
      return;
    }

    let cancelled = false;
    void medplum.readResource('EpisodeOfCare', selectedEpisodeId)
      .then((eoc) => {
        if (!cancelled) setEpisodePaymentType(getPaymentTypeCode(eoc));
      })
      .catch(() => {
        if (!cancelled) setEpisodePaymentType(undefined);
      });

    return () => { cancelled = true; };
  }, [medplum, selectedEpisodeId]);

  useEffect(() => {
    if (!clientId) return;
    // After autosave creates the document, the URL is rewritten from /new to
    // /:responseId. The already-loaded state is current — reloading would
    // remount the live form mid-typing.
    if (responseId && savedResponseRef.current?.id === responseId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        if (responseId) {
          formSessionKeyRef.current = responseId;
          let qr = await medplum.readResource('QuestionnaireResponse', responseId);
          if (qr.subject?.reference !== `Patient/${clientId}`) {
            throw new UserFacingError('This document does not belong to this client.');
          }

          const qRef = qr.questionnaire;
          if (!qRef) throw new Error('Document is missing a template reference.');
          const q = await readQuestionnaireForResponseRef(medplum, qRef);
          if (usesSpecializedTreatmentPlanForm(q)) {
            qr = await hydrateTreatmentPlanQrFromCarePlan(medplum, qr, {
              carePlanVersionId: carePlanVersionId || undefined,
            });
          }

          if (!cancelled) {
            setSavedResponse(qr);
            setSelectedEpisodeId(getChartDocumentEpisodeId(qr));
            setQuestionnaire(q);
            if (carePlanVersionId) {
              setLocalEditMode(false);
            }
          }
        } else if (templateId) {
          formSessionKeyRef.current = 'new';
          medplum.invalidateSearches('Questionnaire');
          const q = await medplum.readResource('Questionnaire', templateId);
          if (!cancelled) setQuestionnaire(q);
        } else {
          throw new Error('No template selected.');
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(getUserFacingErrorMessage(err, 'Could not load the template.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [carePlanVersionId, medplum, clientId, responseId, templateId]);

  const resolvedQuestionnaire = useMemo(() => {
    if (!questionnaire) return null;
    return resolveQuestionnaireVariables(questionnaire, varCtx, { onUnresolved: 'token' });
  }, [questionnaire, varCtx]);

  const author = profile?.resourceType === 'Practitioner' ? profile : null;

  const requireEpisodeSelection = useCallback((): boolean => {
    if (selectedEpisodeId) return true;
    if (providerEpisodes.length === 0) return true;
    notify({
      type: 'warning',
      title: 'Episode required',
      message: 'Select an episode of care you are assigned to.',
    });
    return false;
  }, [providerEpisodes.length, selectedEpisodeId]);

  const persistResponse = useCallback(async (
    nextQr: QuestionnaireResponse,
    options: {
      create?: boolean;
      providerSignRequired: boolean;
      clientSignRequired: boolean;
    },
  ): Promise<QuestionnaireResponse> => {
    if (!clientId) throw new Error('Client not found.');

    const accounts = await getPatientAccountRefs(medplum, clientId);
    const mergedItems = liveResponse?.item ?? nextQr.item;
    let itemsForSave = questionnaire
      ? buildNestedQuestionnaireResponseItemsForSave(questionnaire, mergedItems, savedResponseRef.current?.item) ?? mergedItems
      : mergedItems;
    if (questionnaire && isCoordinationOfCareRoiTemplate(questionnaire)) {
      const prefilled = mergeCoordinationRoiPrefillIntoResponse(
        questionnaire,
        { ...nextQr, item: itemsForSave },
        client?.name ?? 'Client',
      );
      itemsForSave = prefilled.item ?? itemsForSave;
    }
    let payload: QuestionnaireResponse = {
      ...nextQr,
      item: itemsForSave,
      authored: nextQr.authored ?? new Date().toISOString(),
    };

    if (author) {
      const authorRef = practitionerPreferredReference(author);
      payload = {
        ...payload,
        author: authorRef,
        source: authorRef,
      };
    }

    payload = applyChartDocumentSignSettings(payload, {
      providerSignRequired: options.providerSignRequired,
      clientSignRequired: options.clientSignRequired,
    });
    payload = applyChartDocumentEpisode(payload, selectedEpisodeId);
    payload = applyChartDocumentTemplateCategoryTag(payload, questionnaire ?? undefined);
    if (questionnaire && usesSpecializedTreatmentPlanForm(questionnaire)) {
      payload = syncTreatmentPlanExpirationExtension(payload);
      if (aiGenerationProvenance) {
        payload = setTreatmentPlanGenerationProvenance(payload, aiGenerationProvenance);
      }
    }
    payload = {
      ...payload,
      meta: {
        ...(payload.meta ?? {}),
        ...(accounts.length ? { accounts } : {}),
      },
    };

    // Commit the persisted document identity as soon as the write returns — otherwise
    // every retry creates another orphaned QuestionnaireResponse.
    // Do not hydrate-from-CarePlan here either: rebuilt items change `savedResponse`,
    // which re-defaults the live form, fires onChange, and traps autosave on "Saving…".
    // Hydration belongs on load (and version views), not on draft persist.
    if (options.create || !payload.id) {
      const created = await medplum.createResource(payload);
      savedResponseRef.current = created;
      setSavedResponse(created);
      medplum.invalidateSearches('QuestionnaireResponse');
      if (questionnaire && usesSpecializedTreatmentPlanForm(questionnaire)) {
        syncTreatmentPlanCarePlan(medplum);
      }
      return created;
    }

    const latest = await medplum.readResource('QuestionnaireResponse', payload.id);
    const updated = await medplum.updateResource(preserveChartDocumentBasedOn(latest, payload));
    savedResponseRef.current = updated;
    // Keep form seed identity when clinical answers are unchanged so
    // useQuestionnaireForm does not re-default and re-trigger autosave.
    setSavedResponse((prev) => (
      prev?.id === updated.id
      && JSON.stringify(prev.item) === JSON.stringify(updated.item)
        ? prev
        : updated
    ));
    medplum.invalidateSearches('QuestionnaireResponse');
    if (questionnaire && usesSpecializedTreatmentPlanForm(questionnaire)) {
      syncTreatmentPlanCarePlan(medplum);
    }
    return updated;
  }, [aiGenerationProvenance, author, client?.name, clientId, liveResponse?.item, medplum, questionnaire, selectedEpisodeId]);

  const chartDocumentFormSeed = useMemo((): QuestionnaireResponse | null => {
    if (!questionnaire || !clientId || !resolvedQuestionnaire) return savedResponse;
    if (!isCoordinationRoiTemplate) return savedResponse;
    const base = savedResponse ?? buildInitialChartDocumentResponse({
      template: questionnaire,
      patientId: clientId,
      episodeId: selectedEpisodeId,
      author,
      clientSignRequired: true,
    });
    return mergeCoordinationRoiPrefillIntoResponse(
      resolvedQuestionnaire,
      base,
      client?.name ?? 'Client',
    );
  }, [
    author,
    client?.name,
    clientId,
    isCoordinationRoiTemplate,
    questionnaire,
    resolvedQuestionnaire,
    savedResponse,
    selectedEpisodeId,
  ]);

  const buildResponseForSave = useCallback((): QuestionnaireResponse => {
    if (!questionnaire || !clientId) {
      throw new UserFacingError('Document is not ready to save.');
    }
    // Read via ref: consecutive autosaves can run before React re-renders, and
    // a stale `savedResponse` here would rebuild an id-less payload → duplicate create.
    let qr = savedResponseRef.current ?? buildInitialChartDocumentResponse({
      template: questionnaire,
      patientId: clientId,
      episodeId: selectedEpisodeId,
      author,
      clientSignRequired: false,
    });
    if (isCoordinationOfCareRoiTemplate(questionnaire)) {
      const displayName = client?.name ?? 'Client';
      qr = mergeCoordinationRoiPrefillIntoResponse(questionnaire, qr, displayName);
    }
    return qr;
  }, [author, client?.name, clientId, questionnaire, selectedEpisodeId]);

  const buildResponseForValidation = useCallback((): Pick<QuestionnaireResponse, 'item'> | undefined => {
    const items = liveResponse?.item ?? savedResponse?.item;
    if (!items && !liveResponse && !savedResponse) return undefined;
    return { item: items };
  }, [liveResponse, savedResponse]);

  /**
   * Treatment-plan drafts autosave like clinical notes. Signed plans re-opened
   * via Edit (status not `draft`) stay explicit-save only: persisting there
   * mutates the signed original, which must stay intact until the provider
   * deliberately replaces it with Save and Sign.
   */
  const treatmentPlanAutosaveActive =
    isTreatmentPlanTemplate
    && isEditable
    && documentStatus === 'draft'
    && !loading
    && Boolean(questionnaire?.id);

  const buildAutosaveSnapshot = useCallback(() => {
    if (!treatmentPlanAutosaveActive || !questionnaire?.id) return null;
    return buildTreatmentPlanAutosaveSnapshot({
      questionnaireId: questionnaire.id,
      episodeId: selectedEpisodeId,
      items: liveResponse?.item ?? savedResponseRef.current?.item,
      questionnaire,
      existingItems: savedResponseRef.current?.item,
    });
  }, [liveResponse, questionnaire, selectedEpisodeId, treatmentPlanAutosaveActive]);

  const {
    status: autosaveStatus,
    initSnapshot,
    scheduleAutosave,
    flushAutosaveBeforeLeave,
  } = useProviderNoteAutosave({
    enabled: treatmentPlanAutosaveActive && !saving,
    getCurrentSnapshot: buildAutosaveSnapshot,
    persistSave: async () => {
      try {
        let qr = buildResponseForSave();
        qr = setChartDocumentStatus(qr, 'draft');
        // Never create a server draft for a form the provider hasn't touched
        // (snapshot gating should prevent reaching here, this is a backstop).
        const items = liveResponse?.item ?? qr.item;
        if (!savedResponseRef.current?.id && !treatmentPlanHasProviderContent(items)) {
          return null;
        }
        return await persistResponse(qr, {
          create: !savedResponseRef.current?.id,
          providerSignRequired: false,
          clientSignRequired: false,
        });
      } catch {
        return null;
      }
    },
    onSaved: (saved) => {
      // First autosave of a /new document: pin the URL to the created resource
      // so a refresh resumes the same draft. The load effect skips reloading
      // when the id already matches, so the live form does not remount.
      // Promote AI-assistant identity BEFORE navigate so the drawer does not
      // treat the URL change as a different document.
      if (!responseId && saved.id && clientId) {
        setAutosaveCreatedNew(true);
        aiAssistant?.promoteDocumentIdentity(
          saved.id,
          treatmentPlanEditorSessionKeyRef.current,
        );
        navigate(`/dashboard/clients/${clientId}/chart-document/${saved.id}`, { replace: true });
      }
    },
  });

  const scheduleAutosaveRef = useRef(scheduleAutosave);
  scheduleAutosaveRef.current = scheduleAutosave;
  const flushAutosaveRef = useRef(flushAutosaveBeforeLeave);
  flushAutosaveRef.current = flushAutosaveBeforeLeave;

  const handleFormChange = useCallback(() => {
    scheduleAutosaveRef.current();
  }, []);

  /** Baseline once per form session, after the form mounted and applied its defaults. */
  const autosaveBaselineKeyRef = useRef<string | null>(null);
  const autosaveBaselineKey = treatmentPlanAutosaveActive && liveResponse
    ? `${questionnaire?.id}-${formSessionKeyRef.current}`
    : null;
  useEffect(() => {
    if (!autosaveBaselineKey || autosaveBaselineKeyRef.current === autosaveBaselineKey) return;
    const snapshot = buildAutosaveSnapshot();
    if (!snapshot) return;
    autosaveBaselineKeyRef.current = autosaveBaselineKey;
    initSnapshot(snapshot);
  }, [autosaveBaselineKey, buildAutosaveSnapshot, initSnapshot]);

  /** Persist pending edits when the tab hides or the editor unmounts (Back, route change). */
  useEffect(() => {
    if (!treatmentPlanAutosaveActive) return;
    const onPageHide = (): void => {
      void flushAutosaveRef.current();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      void flushAutosaveRef.current();
    };
  }, [treatmentPlanAutosaveActive]);

  const validateBeforeCompletingAction = useCallback((action: ChartDocumentSaveAction): boolean => {
    if (!chartDocumentSaveActionRequiresFieldValidation(action)) return true;
    const questionnaireForValidation = resolvedQuestionnaire ?? questionnaire;
    if (!questionnaireForValidation) return false;

    const validation = validateChartDocumentBeforeSign({
      questionnaire: questionnaireForValidation,
      response: buildResponseForValidation(),
      skipClientFields: saveActionSendsToClient(action),
      episodePaymentType,
      validateTreatmentPlanDiagnosis: usesSpecializedTreatmentPlanUi,
    });
    if (validation.success) return true;

    if (validation.missingItemPath) {
      scrollToAndFlashQuestionnaireItem(validation.missingItemPath);
    }
    notify({
      type: 'warning',
      title: 'Required fields missing',
      // eslint-disable-next-line no-restricted-syntax -- authored validation text from our own code, not a caught error
      message: validation.message,
    });
    return false;
  }, [
    buildResponseForValidation,
    episodePaymentType,
    questionnaire,
    resolvedQuestionnaire,
    usesSpecializedTreatmentPlanUi,
  ]);

  const executeSaveAction = useCallback(async (
    action: ChartDocumentSaveAction,
    signature?: SignatureInfo,
  ) => {
    if (!questionnaire || !clientId || !isEditable || !requireEpisodeSelection()) return;
    if (!validateBeforeCompletingAction(action)) return;

    setSaving(true);
    try {
      // Settle any in-flight/pending autosave first so the explicit save works
      // from the persisted resource (avoids create/update races on new drafts).
      await flushAutosaveRef.current();

      const signSettings = signSettingsForSaveAction(action);
      let qr = buildResponseForSave();

      const wasPreviouslySigned =
        savedResponse != null && getChartDocumentStatus(savedResponse) !== 'draft';

      if (saveActionNeedsSignature(action)) {
        if (!signature) {
          throw new UserFacingError('Provider signature is required for this action.');
        }
        if (wasPreviouslySigned || (isTreatmentPlanTemplate && localEditMode)) {
          qr = clearChartDocumentProviderSignatures(qr);
        }
        qr = applyProviderSignature(qr, signature);
      }

      const sendsToClient = saveActionSendsToClient(action);
      if (sendsToClient && questionnaire && (isClientShareRestrictedTemplate(questionnaire) || isCoordinationOfCareRoiTemplate(questionnaire))) {
        throw new UserFacingError('This document cannot be shared with the client.');
      }

      if (sendsToClient) {
        const itemsForLock =
          buildNestedQuestionnaireResponseItemsForSave(
            questionnaire,
            liveResponse?.item ?? qr.item,
            savedResponse?.item,
          ) ?? liveResponse?.item ?? qr.item;
        const lockedLinkIds = computeProviderLockedLinkIds(questionnaire, {
          ...qr,
          item: itemsForLock,
        });
        qr = setProviderLockedLinkIds(qr, lockedLinkIds);
        qr = setChartDocumentStatus(qr, 'pending-client');
        // A client signature from an earlier send signed the earlier content; the client signs this send afresh.
        qr = clearChartDocumentClientSignature(qr);
      } else if (action === 'save-and-sign') {
        qr = setChartDocumentStatus(qr, 'completed');
      } else {
        if (savedResponse && getChartDocumentStatus(savedResponse) !== 'draft') {
          qr = clearChartDocumentProviderSignatures(qr);
        }
        qr = setChartDocumentStatus(qr, 'draft');
      }

      // ENG-3764 D3 re-sending: a QR whose keyed Task is already closed (completed, then reopened by Request Edits)
      // is never reopened; sending again saves a new QR with the old one's answers and provider signatures, which
      // gets its own identifier and Task. A stopped (cancelled) QR is read-only here (`isEditable`), so never sent.
      const originalBeforeResend = savedResponseRef.current ?? undefined;
      const resendAsNew = sendsToClient
        && await chartDocumentNeedsNewQrToResend(medplum, originalBeforeResend);
      if (resendAsNew) {
        qr = resentChartDocumentCopy(qr);
      }

      const saved = await persistResponse(qr, {
        create: !savedResponseRef.current?.id || resendAsNew,
        ...signSettings,
      });

      if (resendAsNew) {
        // The copy exists: retire the original so the chart shows one document.
        try {
          await supersedeResentChartDocument(medplum, originalBeforeResend, saved.id);
        } catch (err) {
          console.error(err);
          notify({ type: 'warning', title: 'Document sent', message: 'The previous copy of this document could not be hidden from the chart.' });
        }
      }
      // Move to the copy before waiting on its Task: a failed or slow wait must not leave the page on the retired original.
      const resentHref = resentChartDocumentHref(clientId, resendAsNew, saved.id);
      if (resentHref) navigate(resentHref, { replace: true });

      if (sendsToClient) {
        // ENG-3764 D1: the pending-client save is the event; the task-events listener creates the
        // signature-request or document-completion Task and its client notification. Wait under both systems: an
        // in-place re-send that flips the client-signature setting keeps the open Task under the other one.
        if (!saved.id) throw new Error('Failed to save document before sending.');
        const outcome = await waitForTaskForQr(medplum, saved.id, CHART_DOCUMENT_TASK_IDENTIFIER_SYSTEMS);
        if (outcome.kind === 'error' || outcome.kind === 'already-assigned') {
          throw new UserFacingError(outcome.message);
        }
        if (outcome.kind === 'timeout') {
          // eslint-disable-next-line no-restricted-syntax -- authored outcome text from clientAssignmentTasks, not a caught error
          notify({ type: 'warning', title: 'Document saved', message: outcome.message });
        }
      }

      if (action === 'save') {
        notify({ type: 'success', title: 'Saved', message: 'Your changes were saved.' });
        if (isNew && saved.id) {
          navigate(`/dashboard/clients/${clientId}/chart-document/${saved.id}`, { replace: true });
        }
        return;
      }

      notify({
        type: 'success',
        title: 'Document saved',
        message: chartDocumentSaveSuccessMessage(action),
      });

      // Signing a treatment plan keeps the provider on the document. Leaving edit mode
      // flips the form to the read-only signed view built from the saved response.
      if (isTreatmentPlanTemplate && action === 'save-and-sign') {
        setLocalEditMode(false);
        if (isNew && saved.id) {
          navigate(`/dashboard/clients/${clientId}/chart-document/${saved.id}`, { replace: true });
        }
        return;
      }

      navigate(chartHref);
    } catch (err) {
      notify({
        type: 'error',
        title: 'Save failed',
        error: err,
      });
    } finally {
      setSaving(false);
      setPendingAction(null);
    }
  }, [
    buildResponseForSave,
    chartHref,
    clientId,
    isEditable,
    isNew,
    liveResponse?.item,
    medplum,
    navigate,
    persistResponse,
    questionnaire,
    requireEpisodeSelection,
    isTreatmentPlanTemplate,
    localEditMode,
    savedResponse,
    validateBeforeCompletingAction,
  ]);

  const handleStartEditingTreatmentPlan = () => {
    setLocalEditMode(true);
  };

  const handleApplyAiSuggestion = useCallback((suggestion: TreatmentPlanRevisionSuggestion) => {
    setPendingAiSuggestion(suggestion);
  }, []);

  /** Guided AI planner result → apply operations to the live form + record provenance for save. */
  const handlePlanGenerated = useCallback((generated: TreatmentPlanGeneratorSuccessResult) => {
    const createSession = isNew || autosaveCreatedNew;
    setPendingAiSuggestion({
      summary: generated.summary,
      warnings: generated.warnings,
      operations: generated.operations,
      // Re-running guided/quick onto an already-filled draft must replace, not append.
      replaceExistingContent: guidedGeneratedApplied || !createSession,
    });
    setGuidedGeneratedApplied(true);
    setAiGenerationProvenance({
      generator: 'ai-treatment-plan-generator',
      generatedAt: new Date().toISOString(),
      model: generated.model,
      promptVersion: generated.promptVersion,
      providerReviewed: false,
    });
  }, [guidedGeneratedApplied, isNew, autosaveCreatedNew]);

  const treatmentPlanDocumentKey = responseId ?? 'new';

  /** Best-effort clinical context parsed from the live form, passed to the AI so it skips re-asking known facts. */
  const treatmentPlanClinicalContext = useMemo(() => {
    if (!usesSpecializedTreatmentPlanUi) return undefined;
    const draft = parseTreatmentPlanDraft(liveResponse?.item ?? savedResponse?.item);
    return {
      diagnoses: diagnosesFromTreatmentPlanDraftCodes(draft.diagnoses.map((d) => d.code)),
      symptoms: symptomsFromTreatmentPlanUiTexts(
        draft.diagnoses.flatMap((d) => d.behavioralDefinitions.map((b) => b.text)),
      ),
      treatmentFrequency: draft.treatmentFrequency,
    };
  }, [usesSpecializedTreatmentPlanUi, liveResponse?.item, savedResponse?.item]);

  /**
   * Create-session docs (opened via /new, including after diagnosis autosave pins a
   * response id) keep Quick generate. Only flip to Revise after AI generation this
   * session, or when opening an already-persisted document from the chart.
   */
  const isTreatmentPlanCreateSession = isNew || autosaveCreatedNew;
  const treatmentPlanHasExistingPlan =
    guidedGeneratedApplied || !isTreatmentPlanCreateSession;
  // Keep operation=initial through first AI draft generation. Autosave pinning a QR id
  // must not suppress intake-transcript lookup during Quick/Guided create.
  const treatmentPlanOperation: TreatmentPlanOperation =
    isTreatmentPlanCreateSession && !guidedGeneratedApplied ? 'initial' : 'revision';
  // Register revision + guided contexts with the shell AI assistant drawer.
  useEffect(() => {
    if (!aiAssistant) return;
    if (!clientId || !questionnaire || !usesSpecializedTreatmentPlanUi) {
      aiAssistant.registerRevisionContext(null);
      return;
    }
    aiAssistant.registerRevisionContext({
      kind: 'treatment-plan-revision',
      patientId: clientId,
      documentKey: treatmentPlanDocumentKey,
      episodeId: selectedEpisodeId,
      editorSessionKey: treatmentPlanEditorSessionKeyRef.current,
      questionnaire,
      getSavedResponse: () => savedResponseRef.current,
      getLiveResponse: () => liveResponseRef.current,
      onApplySuggestions: handleApplyAiSuggestion,
      disabled: !isEditable,
      geminiAiDisabled: geminiAiDisabled || !isEditable,
      readOnly: !isEditable,
      hasExistingPlan: treatmentPlanHasExistingPlan,
    });
    return () => {
      aiAssistant.registerRevisionContext(null);
    };
  }, [
    aiAssistant,
    clientId,
    selectedEpisodeId,
    questionnaire,
    usesSpecializedTreatmentPlanUi,
    isEditable,
    treatmentPlanDocumentKey,
    geminiAiDisabled,
    handleApplyAiSuggestion,
    treatmentPlanHasExistingPlan,
  ]);

  useEffect(() => {
    if (!aiAssistant) return;
    if (
      !clientId
      || !selectedEpisodeId
      || !questionnaire?.id
      || !usesSpecializedTreatmentPlanUi
      || !isEditable
    ) {
      aiAssistant.registerGuidedContext(null);
      return;
    }
    // Page registration supplies clinical context only — never sets mode.
    // Explicit Quick/Guided launches own the active flow type.
    aiAssistant.registerGuidedContext({
      kind: 'treatment-plan-guided-create',
      patientId: clientId,
      documentKey: treatmentPlanDocumentKey,
      editorSessionKey: treatmentPlanEditorSessionKeyRef.current,
      episodeId: selectedEpisodeId,
      questionnaireId: questionnaire.id,
      geminiAiDisabled,
      onPlanGenerated: handlePlanGenerated,
      diagnoses: treatmentPlanClinicalContext?.diagnoses,
      symptoms: treatmentPlanClinicalContext?.symptoms,
      operation: treatmentPlanOperation,
      treatmentFrequency: treatmentPlanClinicalContext?.treatmentFrequency,
    });
    return () => {
      aiAssistant.registerGuidedContext(null);
    };
  }, [
    aiAssistant,
    clientId,
    selectedEpisodeId,
    questionnaire?.id,
    usesSpecializedTreatmentPlanUi,
    isEditable,
    treatmentPlanDocumentKey,
    geminiAiDisabled,
    handlePlanGenerated,
    treatmentPlanClinicalContext,
    treatmentPlanOperation,
  ]);

  const handleAiSuggestionApplied = useCallback(() => {
    setPendingAiSuggestion(null);
  }, []);

  const handleAiSuggestionError = useCallback((message: string) => {
    notify({
      title: 'Could not apply AI suggestions',
      message,
      type: 'error',
    });
    setPendingAiSuggestion(null);
  }, []);

  const handleSaveActionSelect = (action: ChartDocumentSaveAction) => {
    if (isTreatmentPlanTemplate && action !== 'save-and-sign' && action !== 'save') return;
    if (isCoordinationRoiTemplate && action !== 'save' && action !== SEND_FOR_CLIENT_SIGNATURE_ACTION) return;
    if (questionnaire && isClientShareRestrictedTemplate(questionnaire) && saveActionSendsToClient(action)) {
      return;
    }
    if (!requireEpisodeSelection()) return;
    if (!validateBeforeCompletingAction(action)) return;
    if (saveActionSendsToClient(action)) {
      setPendingSendAction(action);
      openSendConfirm();
      return;
    }
    if (saveActionNeedsSignature(action)) {
      setPendingAction(action);
      openSign();
      return;
    }
    void executeSaveAction(action);
  };

  const handleSendConfirmCancel = () => {
    closeSendConfirm();
    setPendingSendAction(null);
  };

  const handleSendConfirm = () => {
    const action = pendingSendAction;
    closeSendConfirm();
    setPendingSendAction(null);
    if (!action) return;
    if (!validateBeforeCompletingAction(action)) return;
    if (saveActionNeedsSignature(action)) {
      setPendingAction(action);
      openSign();
      return;
    }
    void executeSaveAction(action);
  };

  const handleSignComplete = (signature: SignatureInfo) => {
    closeSign();
    if (pendingAction) {
      void executeSaveAction(pendingAction, signature);
    }
  };

  if (!clientId) {
    return <Navigate to="/dashboard/caseload" replace />;
  }

  if (!client) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={18} />} color="red" title="Client not found">
          Unable to load this client chart.
        </Alert>
      </Container>
    );
  }

  const isTreatmentPlanDocument = isTreatmentPlanTemplate;
  const showTreatmentPlanSpecializedUi = usesSpecializedTreatmentPlanUi;
  const showTreatmentPlanSideRail =
    showTreatmentPlanSpecializedUi && isEditable && !aiAssistant?.open;
  const documentTitle = questionnaire?.title ?? questionnaire?.name ?? 'Chart document';
  const sendConfirmCopy = pendingSendAction
    ? sendToClientConfirmCopy(pendingSendAction, documentTitle, client.name)
    : null;
  const formKey = `${resolvedQuestionnaire?.id ?? questionnaire?.id ?? 'template'}-${formSessionKeyRef.current}-${isEditable ? 'edit' : 'view'}`;
  const chartDocumentIntro = (
    <>
      {questionnaire?.description && (
        <Text size="sm" c="gray.7">{questionnaire.description}</Text>
      )}

      {localEditMode && isTreatmentPlanDocument && documentStatus !== 'draft' && (
        <Alert color="blue" variant="light" title="Editing treatment plan">
          Your previous signature will be removed when you save. Use Save and Sign to apply your changes.
        </Alert>
      )}

      {isNew && usesSpecializedTreatmentPlanUi ? (
        <ActiveTreatmentPlanDiagnosesBanner
          diagnoses={activeTreatmentPlanDiagnoses}
          loading={activeDiagnosesLoading}
        />
      ) : null}

      {episodeSelectData.length > 0 && (
        <Select
          label="Episode of care"
          placeholder="Select episode"
          data={episodeSelectData}
          value={selectedEpisodeId ?? null}
          onChange={(value) => {
            setSelectedEpisodeId(value ?? undefined);
            scheduleAutosaveRef.current();
          }}
          disabled={!isEditable}
          required
        />
      )}
    </>
  );
  const providerSignature = savedResponse
    ? getSignatureFromQR(savedResponse, PROVIDER_SIGNATURE_EXT_URL)
    : undefined;
  const supervisorSignature = savedResponse
    ? getSignatureFromQR(savedResponse, SUPERVISOR_SIGNATURE_EXT_URL)
    : undefined;
  const clientSignature = savedResponse ? getChartDocumentClientSignature(savedResponse) : undefined;
  const hideStaleSignaturesWhileEditing =
    localEditMode && documentStatus !== 'draft';
  const showProviderSignature = Boolean(providerSignature) && !hideStaleSignaturesWhileEditing;
  const showSupervisorSignature = Boolean(supervisorSignature) && !hideStaleSignaturesWhileEditing;
  const showClientSignature =
    !hideStaleSignaturesWhileEditing
    && (
      Boolean(clientSignature)
      || (savedResponse != null && isChartDocumentClientSignatureRequired(savedResponse))
    );
  const signatureColumnCount =
    Number(showProviderSignature) + Number(showSupervisorSignature) + Number(showClientSignature);
  const signatureColSpan = signatureColumnCount >= 2 ? 6 : 12;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Button
          component={Link}
          to={chartHref}
          variant="subtle"
          color="gold.7"
          leftSection={<IconArrowLeft size={16} />}
          style={{ alignSelf: 'flex-start' }}
        >
          Back to chart
        </Button>

        <RecordPageHeader
          recordType={isTreatmentPlanDocument ? 'Treatment Plan' : 'Chart document'}
          recordTypeColor={isTreatmentPlanDocument ? 'blue' : 'purple'}
          icon={<IconFileText size={22} color="var(--mantine-color-violet-7)" />}
          primaryTitle={documentTitle}
          secondaryTitle={client.name}
          clientHref={chartHref}
          clientPreferredName={client.name}
          clientPronouns={client.pronouns}
          rightSide={(
            <Group gap="sm">
              {canStartEditingTreatmentPlan && (
                <Button
                  variant="filled"
                  color="gold"
                  size="sm"
                  leftSection={<IconEdit size={16} />}
                  onClick={handleStartEditingTreatmentPlan}
                >
                  Edit
                </Button>
              )}
              {isHistoricalImport && (
                <Badge color="gray" variant="light" size="lg">Historical</Badge>
              )}
              <Badge color={statusBadgeColor(documentStatus)} variant="light" size="lg">
                {CHART_DOCUMENT_STATUS_LABELS[documentStatus]}
              </Badge>
            </Group>
          )}
        />

        {loading && (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading document…</Text>
          </Group>
        )}

        {!loading && loadError && (
          <Alert icon={<IconAlertCircle size={18} />} color="red" title="Unable to load document">
            {loadError}
          </Alert>
        )}

        {!loading && !loadError && blockedTreatmentPlanAuthoring && (
          <Alert icon={<IconAlertCircle size={18} />} color="yellow" title="Treatment plan not available">
            Treatment plans are created by the clinician assigned to this episode of care.
          </Alert>
        )}

        {!loading && !loadError && !blockedTreatmentPlanAuthoring && resolvedQuestionnaire && (
          <Grid gutter="lg" align="flex-start">
            {showTreatmentPlanSideRail && (
              <Grid.Col span={{ base: 12, md: 4 }} order={{ base: 1, md: 2 }}>
                <Stack gap="md" style={{ position: 'sticky', top: 'var(--mantine-spacing-md)' }}>
                  <AiTreatmentPlanGeneratorPanel
                    patientId={clientId}
                    episodeId={selectedEpisodeId}
                    questionnaireId={questionnaire?.id}
                    documentKey={treatmentPlanDocumentKey}
                    editorSessionKey={treatmentPlanEditorSessionKeyRef.current}
                    geminiAiDisabled={geminiAiDisabled}
                    onPlanGenerated={handlePlanGenerated}
                    generatedApplied={guidedGeneratedApplied}
                    hasExistingPlan={treatmentPlanHasExistingPlan}
                    diagnoses={treatmentPlanClinicalContext?.diagnoses}
                    symptoms={treatmentPlanClinicalContext?.symptoms}
                    operation={treatmentPlanOperation}
                    treatmentFrequency={treatmentPlanClinicalContext?.treatmentFrequency}
                  />
                  <TreatmentPlanQualityPanel
                    items={liveResponse?.item ?? savedResponse?.item}
                  />
                </Stack>
              </Grid.Col>
            )}
            <Grid.Col
              span={{
                base: 12,
                md: showTreatmentPlanSideRail ? 8 : 12,
              }}
              order={{ base: 2, md: 1 }}
            >
              <Paper
                withBorder={!showTreatmentPlanSpecializedUi}
                p={showTreatmentPlanSpecializedUi ? 0 : 'lg'}
                style={
                  showTreatmentPlanSpecializedUi
                    ? { background: 'transparent', boxShadow: 'none' }
                    : undefined
                }
              >
                <Stack gap="xl">
                  {showTreatmentPlanSpecializedUi ? (
                    <TreatmentPlanDocumentFormSection
                      key={formKey}
                      questionnaire={resolvedQuestionnaire}
                      savedResponse={savedResponse}
                      readOnlyLinkIds={readOnlyLinkIds}
                      isEditable={isEditable}
                      varCtx={varCtx}
                      headerContent={chartDocumentIntro}
                      onLiveResponseChange={setLiveResponse}
                      onFormChange={handleFormChange}
                      pendingAiSuggestion={pendingAiSuggestion}
                      onAiSuggestionApplied={handleAiSuggestionApplied}
                      onAiSuggestionError={handleAiSuggestionError}
                    />
                  ) : (
                    <>
                      {chartDocumentIntro}
                      <ChartDocumentFormSection
                        key={formKey}
                        questionnaire={resolvedQuestionnaire}
                        formSeed={chartDocumentFormSeed}
                        readOnlyLinkIds={readOnlyLinkIds}
                        isEditable={isEditable}
                        varCtx={varCtx}
                        patientId={clientId}
                        deferRequiredFieldValidation={deferRequiredFieldValidation}
                        onLiveResponseChange={setLiveResponse}
                        onFormChange={handleFormChange}
                      />
                    </>
                  )}

                  {(showProviderSignature || showSupervisorSignature || showClientSignature) && (
                <Grid align="stretch">
                  {showProviderSignature && providerSignature && (
                    <Grid.Col
                      span={{ base: 12, sm: signatureColSpan }}
                      style={signatureColumnCount >= 2 ? { display: 'flex' } : undefined}
                    >
                      <SignatureDisplayBox
                        signature={providerSignature}
                        label="Provider signature"
                        fullHeight={signatureColumnCount >= 2}
                      />
                    </Grid.Col>
                  )}
                  {showSupervisorSignature && supervisorSignature && (
                    <Grid.Col
                      span={{ base: 12, sm: signatureColSpan }}
                      style={signatureColumnCount >= 2 ? { display: 'flex' } : undefined}
                    >
                      <SignatureDisplayBox
                        signature={supervisorSignature}
                        label="Supervisor signature"
                        fullHeight={signatureColumnCount >= 2}
                      />
                    </Grid.Col>
                  )}
                  {showClientSignature && (
                    <Grid.Col
                      span={{ base: 12, sm: signatureColSpan }}
                      style={signatureColumnCount >= 2 ? { display: 'flex' } : undefined}
                    >
                      <SignatureDisplayBox
                        signature={clientSignature}
                        label="Client signature"
                        fullHeight={signatureColumnCount >= 2}
                      />
                    </Grid.Col>
                  )}
                </Grid>
              )}

              <DocumentFormatFooter footerText={docFormat.footerText} />

              {isEditable && (
                <Group justify="space-between" wrap="wrap">
                  <Button variant="default" onClick={() => navigate(chartHref)} disabled={saving}>
                    Back
                  </Button>
                  {isTreatmentPlanDocument ? (
                    <Group gap="sm">
                      {treatmentPlanAutosaveActive && <NoteAutosaveStatus status={autosaveStatus} />}
                      <Button
                        variant="default"
                        loading={saving}
                        onClick={() => handleSaveActionSelect('save')}
                      >
                        Save draft
                      </Button>
                      <Button
                        loading={saving}
                        onClick={() => handleSaveActionSelect('save-and-sign')}
                      >
                        Save and Sign
                      </Button>
                    </Group>
                  ) : (
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <Button
                          rightSection={<IconChevronDown size={16} />}
                          loading={saving}
                        >
                          Save
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {availableSaveActions.map(({ action, label }) => (
                          <Menu.Item
                            key={action}
                            onClick={() => handleSaveActionSelect(action)}
                          >
                            {label}
                          </Menu.Item>
                        ))}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              )}
            </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        )}
      </Stack>

      <Modal
        opened={sendConfirmOpened}
        onClose={handleSendConfirmCancel}
        title={sendConfirmCopy?.title ?? 'Send to client?'}
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="gray.7">
            {sendConfirmCopy?.body ?? 'This will send the document to the client in the client portal.'}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleSendConfirmCancel}>
              Cancel
            </Button>
            <Button onClick={handleSendConfirm}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SignatureCaptureModal
        opened={signOpened}
        onClose={() => { closeSign(); setPendingAction(null); }}
        onSign={handleSignComplete}
        title="Sign document"
        submitLabel="Sign and continue"
        jurisdictionState={clientJurisdictionState}
      />
    </Container>
  );
}
